/**
 * How much more room a shape needs around its text.
 *
 * A rectangle's interior *is* its bounding box; a diamond's is half of it, and text centred in
 * one overflows the sides long before it reaches the edge of the box. So the padding is scaled
 * per shape and the box grows — rather than the text being clipped, or the shape being drawn
 * bigger than the space the layout reserved for it.
 *
 * The diamond's numbers are the honest ones: a rectangle inscribed in a diamond has half its
 * width and half its height, so containing the same text needs roughly twice as much of each.
 * Doubling exactly makes for enormous nodes, and these are the compromise that keeps a line of
 * text inside the shape without the shape swallowing the map.
 */
const PADDING_SCALE = {
    rectangle: { x: 1, y: 1 },
    'rounded-rectangle': { x: 1, y: 1 },
    square: { x: 1, y: 1 },
    circle: { x: 1.6, y: 1.6 },
    ellipse: { x: 1.4, y: 1.4 },
    capsule: { x: 1.3, y: 1 },
    diamond: { x: 3.2, y: 2.6 },
    hexagon: { x: 2, y: 1 },
    octagon: { x: 1.4, y: 1.2 },
    cloud: { x: 2.4, y: 2 },
    card: { x: 1, y: 1 },
    'sticky-note': { x: 1, y: 1 },
    balloon: { x: 1.2, y: 1.3 },
    'invisible-frame': { x: 1, y: 1 },
};
export function paddingScaleFor(shape) {
    return PADDING_SCALE[shape];
}
/**
 * The outline for a shape, or `null` for the rounded rectangle every renderer draws natively.
 *
 * A table rather than a `switch`, and every member of the union is listed: a fifteenth shape
 * cannot be added to the catalogue without somebody deciding here what it looks like, which is
 * the same guarantee the undo wording gets from its own table. `null` is a decision — "a rounded
 * rectangle is right for this one" — not an omission.
 */
const OUTLINES = {
    rectangle: null,
    'rounded-rectangle': null,
    square: null,
    circle: null,
    ellipse: null,
    capsule: null,
    diamond,
    hexagon,
    octagon: null,
    cloud,
    card: null,
    'sticky-note': null,
    balloon: null,
    'invisible-frame': null,
};
export function outlineFor(shape, rect) {
    const draw = OUTLINES[shape];
    return draw === null ? null : draw(rect);
}
function move(to) {
    return { kind: 'move', to };
}
function line(to) {
    return { kind: 'line', to };
}
/** Four corners, on the midpoints of the box's sides. */
function diamond(rect) {
    const midX = rect.x + rect.width / 2;
    const midY = rect.y + rect.height / 2;
    return [
        move({ x: midX, y: rect.y }),
        line({ x: rect.x + rect.width, y: midY }),
        line({ x: midX, y: rect.y + rect.height }),
        line({ x: rect.x, y: midY }),
        line({ x: midX, y: rect.y }),
    ];
}
/**
 * A flat-topped hexagon: points on the left and right, flat edges top and bottom.
 *
 * Flat-topped rather than pointy-topped because a node is a box of text, and this is the
 * orientation whose interior is widest where the words are. The inset is capped at half the
 * height so a short wide node degrades into a diamond rather than into a self-crossing outline.
 */
function hexagon(rect) {
    const inset = Math.min(rect.width * 0.2, rect.height / 2);
    const midY = rect.y + rect.height / 2;
    const right = rect.x + rect.width;
    const bottom = rect.y + rect.height;
    return [
        move({ x: rect.x + inset, y: rect.y }),
        line({ x: right - inset, y: rect.y }),
        line({ x: right, y: midY }),
        line({ x: right - inset, y: bottom }),
        line({ x: rect.x + inset, y: bottom }),
        line({ x: rect.x, y: midY }),
        line({ x: rect.x + inset, y: rect.y }),
    ];
}
/**
 * How far a lobe's control points sit off its chord, as a multiple of the bump radius.
 *
 * A cubic with both controls offset by `b` perpendicular to its chord peaks at three quarters of
 * `b`. So offsetting by four thirds of the radius makes the lobe peak at *exactly* the radius —
 * which is exactly the inset the ring was walked at, and therefore exactly the edge of the box.
 * Deriving it from the chord instead is what made the first version overflow: a wide node has
 * long chords, and long chords bulged past the box and onto the node beside it.
 */
const BUMP = 4 / 3;
/**
 * A cloud: the box's perimeter walked in steps, with each step bulging outward.
 *
 * The bumps are cubic arcs rather than real circles, and deliberately: an exact arc is not
 * expressible in the three primitives every renderer here shares, and a shape drawn from a
 * formula all three run is worth more than one that is geometrically perfect in one of them. The
 * constant above is what makes a step look like a lobe rather than a wave.
 *
 * The walk runs around a rectangle inset by the bump radius, so the lobes bulge out to the box's
 * edge and no further — the box is what the layout reserved, and a shape that overflowed it would
 * overlap the node next to it.
 */
function cloud(rect) {
    const across = Math.max(3, Math.round(rect.width / 44));
    const down = Math.max(2, Math.round(rect.height / 44));
    const radius = Math.min(rect.width / (2 * across), rect.height / (2 * down));
    const left = rect.x + radius;
    const top = rect.y + radius;
    const right = rect.x + rect.width - radius;
    const bottom = rect.y + rect.height - radius;
    // The perimeter as a closed ring of points, corners included once each. Walking it in order is
    // what makes the lobes meet cleanly where two edges join.
    const ring = [];
    const step = (from, to, count) => {
        for (let index = 0; index < count; index += 1) {
            ring.push({
                x: from.x + ((to.x - from.x) * index) / count,
                y: from.y + ((to.y - from.y) * index) / count,
            });
        }
    };
    step({ x: left, y: top }, { x: right, y: top }, across);
    step({ x: right, y: top }, { x: right, y: bottom }, down);
    step({ x: right, y: bottom }, { x: left, y: bottom }, across);
    step({ x: left, y: bottom }, { x: left, y: top }, down);
    const first = ring[0];
    // Unreachable — the walk always emits at least five points — and cheaper to answer than to
    // assert about.
    if (first === undefined)
        return [];
    const segments = [move(first)];
    for (const [index, from] of ring.entries()) {
        // Wraps to the start, which is what closes the ring. The fallback is unreachable and is a
        // degenerate chord rather than an assertion — `!` is not used in this codebase.
        const to = ring[(index + 1) % ring.length] ?? from;
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const length = Math.hypot(dx, dy) || 1;
        // The outward normal of this chord. The ring is wound clockwise in screen coordinates, where
        // y grows downwards, so the outward side is the one this rotation lands on.
        const normal = { x: dy / length, y: -dx / length };
        const bulge = radius * BUMP;
        segments.push({
            kind: 'cubic',
            control1: { x: from.x + normal.x * bulge, y: from.y + normal.y * bulge },
            control2: { x: to.x + normal.x * bulge, y: to.y + normal.y * bulge },
            to,
        });
    }
    return segments;
}
//# sourceMappingURL=shapes.js.map